// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hidePdfPopup } from "../../store/auth/action";
import RelativeLoader from "../loaders/RelativeLoader";
import { Document, Page, pdfjs } from "react-pdf";
import { MdClose } from "react-icons/md";
import {
  LuMinus,
  LuPlus,
  LuDownload,
  LuPrinter,
  LuMoreVertical,
  LuMaximize2,
  LuMinimize2,
  LuPanelLeftClose,
  LuPanelLeftOpen,
} from "react-icons/lu";

// Served from this app's own origin (public/pdf.worker.min.js, copied from the
// installed pdfjs-dist version) instead of a CDN - the app's CSP only allows
// worker-src 'self', so an external unpkg.com/etc. URL would always be blocked.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const HWAI_ICON_URL = "https://hwlobbystorer1.blob.core.windows.net/sectionicons/hwaiicon.png?v=1";

const ZOOM_STEP = 10;
const ZOOM_MIN = 50;
const ZOOM_MAX = 200;

// Reusable PDF popup - opened from anywhere a "releasenote" (PDF) dashboard is
// clicked, via the isPdfPop/pdfPopupContent redux state (see handleItem.js). The
// dashboard behind it stays mounted and visible; this only overlays on top.
const PdfViewerPopup = () => {
  const dispatch = useDispatch();
  const { isPdfPop, pdfPopupContent } = useSelector((state) => ({
    isPdfPop: state.isPdfPop,
    pdfPopupContent: state.pdfPopupContent,
  }));

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [zoom, setZoom] = useState(100);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [maximized, setMaximized] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const pageRefs = useRef({});

  const url = pdfPopupContent?.url;
  const categoryLabel = pdfPopupContent?.categoryLabel;
  const rawLabel = pdfPopupContent?.label || "Document";
  // Some dashboard labels already bake the category name in (e.g. "Release Notes ·
  // July 2023") - strip that repeated prefix first so combining it with
  // categoryLabel below never doubles it up (e.g. "Release Notes · Release Notes ·
  // July 2023"), then always rebuild the single "Category · Item" title line.
  const shortLabel = categoryLabel
    ? rawLabel.replace(new RegExp(`^\\s*${categoryLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[·:/-]\\s*`, "i"), "")
    : rawLabel;
  // The "Resources" category is a generic bucket, not a meaningful prefix - drop it
  // so those titles stay just the document name.
  const showCategory = Boolean(categoryLabel) && categoryLabel.trim().toLowerCase() !== "resources";
  const title = showCategory ? `${categoryLabel} · ${shortLabel}` : shortLabel;

  useEffect(() => {
    if (isPdfPop) {
      setNumPages(0);
      setPageNumber(1);
      setPageInput("1");
      setZoom(100);
      setLoadFailed(false);
      setMaximized(false);
    }
  }, [isPdfPop, url]);

  if (!isPdfPop) return null;

  const close = () => dispatch(hidePdfPopup());

  const goToPage = (n) => {
    if (!numPages) return;
    const clamped = Math.min(Math.max(1, n), numPages);
    setPageNumber(clamped);
    setPageInput(String(clamped));
    pageRefs.current[clamped]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDownload = () => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = title;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handlePrint = () => {
    if (!url) return;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };
  };

  return (
    <div
      className={`fixed inset-0 z-[100] bg-black/50 flex items-center justify-center ani ${
        maximized ? "p-0" : "p-6"
      }`}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`font-geist bg-general-background flex flex-col justify-start items-start overflow-hidden ani ${
          maximized
            ? "w-full h-full rounded-none"
            : "w-[980px] max-w-full h-[760px] max-h-full rounded-[10px] shadow-lg outline outline-1 outline-offset-[-1px] outline-general-border"
        }`}
      >
        <div className="self-stretch px-4 py-2.5 flex flex-col justify-between items-start">
          <div className="self-stretch inline-flex justify-between items-center gap-4">
            <div className="min-w-0 flex-1 flex items-center gap-2">
              {/* Maximized fills the whole screen, so the popup reads as a page of its
                  own - the product mark next to the title keeps it branded there. */}
              {maximized && (
                <img
                  alt=""
                  src={HWAI_ICON_URL}
                  className="shrink-0 size-5 object-contain select-none"
                />
              )}
              <div className="min-w-0 flex-1 text-left text-general-foreground text-base font-semibold leading-6 truncate">
                {title}
              </div>
            </div>
            <div className="shrink-0 flex justify-end items-center gap-2">
              <button
                type="button"
                onClick={() => setMaximized((v) => !v)}
                className="p-1.5 rounded-lg hover:bg-general-accent ani text-general-muted-foreground"
              >
                {maximized ? <LuMinimize2 className="size-4" /> : <LuMaximize2 className="size-4" />}
              </button>
              <button
                type="button"
                onClick={close}
                className="p-1.5 rounded-lg hover:bg-general-accent ani text-general-muted-foreground"
              >
                <MdClose className="size-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="self-stretch h-px bg-general-border" />

        {!loadFailed && numPages > 0 && (
          <div className="self-stretch px-4 py-3 flex justify-start items-center gap-4">
            <button
              type="button"
              onClick={() => setShowThumbnails((v) => !v)}
              title={showThumbnails ? "Hide thumbnails" : "Show thumbnails"}
              className="size-9 min-w-8 min-h-8 p-2 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
            >
              {showThumbnails ? (
                <LuPanelLeftClose className="size-4 text-general-muted-foreground" />
              ) : (
                <LuPanelLeftOpen className="size-4 text-general-muted-foreground" />
              )}
            </button>

            <div className="flex-1 flex justify-start items-center gap-5">
              <div className="flex justify-start items-center gap-2">
                <div className="text-general-muted-foreground text-sm font-normal leading-5">Page</div>
                <input
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ""))}
                  onBlur={() => goToPage(Number(pageInput) || 1)}
                  onKeyDown={(e) => e.key === "Enter" && goToPage(Number(pageInput) || 1)}
                  className="w-12 min-h-9 px-3 py-2 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border text-center text-general-foreground text-sm font-normal leading-5"
                />
                <div className="text-general-muted-foreground text-sm font-normal leading-5">of {numPages || "-"}</div>
              </div>

              <div className="flex justify-start items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
                  className="size-9 min-w-8 min-h-8 p-2 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
                >
                  <LuMinus className="size-4 text-general-muted-foreground" />
                </button>
                <div className="w-16 min-h-9 px-3 py-2 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border flex justify-center items-center">
                  <span className="text-general-foreground text-sm font-normal leading-5">{zoom}%</span>
                </div>
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
                  className="size-9 min-w-8 min-h-8 p-2 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
                >
                  <LuPlus className="size-4 text-general-muted-foreground" />
                </button>
              </div>
            </div>

            <div className="flex justify-start items-center gap-3">
              <button
                type="button"
                onClick={handleDownload}
                title="Download"
                className="min-w-8 min-h-8 p-2 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
              >
                <LuDownload className="size-4 text-general-muted-foreground" />
              </button>
              <button
                type="button"
                onClick={handlePrint}
                title="Print"
                className="min-w-8 min-h-8 p-2 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
              >
                <LuPrinter className="size-4 text-general-muted-foreground" />
              </button>
              <button
                type="button"
                title="More options"
                className="min-w-8 min-h-8 p-2 rounded-lg hover:bg-general-accent ani flex justify-center items-center"
              >
                <LuMoreVertical className="size-4 text-general-muted-foreground" />
              </button>
            </div>
          </div>
        )}

        <div className="self-stretch flex-1 min-h-0 inline-flex justify-center items-start overflow-hidden">
          {!url ? (
            <div className="flex-1 self-stretch flex items-center justify-center text-general-muted-foreground text-sm">
              No document to show.
            </div>
          ) : loadFailed ? (
            // Falls back to a plain iframe embed (the old behavior) whenever react-pdf
            // can't fetch the file directly - e.g. the storage host's CORS rules don't
            // allow this origin. An iframe navigation isn't subject to CORS the way a
            // fetch() is, so it still works even when the fancy viewer can't load.
            <iframe
              title={title}
              src={url}
              className="flex-1 self-stretch w-full h-full border-0"
            />
          ) : (
            <Document
              file={url}
              onLoadSuccess={(pdf) => setNumPages(pdf.numPages)}
              onLoadError={() => setLoadFailed(true)}
              loading={
                <div className="flex-1 self-stretch flex items-center justify-center p-6">
                  <RelativeLoader />
                </div>
              }
              error={
                <div className="flex-1 self-stretch flex items-center justify-center p-6">
                  <RelativeLoader />
                </div>
              }
              className="self-stretch flex-1 min-h-0 flex justify-center items-start"
            >
              {showThumbnails && numPages > 0 && (
                <div className="app-scrollbar w-40 self-stretch px-4 py-3 bg-[#F5F5F5] flex flex-col justify-start items-center gap-3 overflow-y-auto">
                  {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => goToPage(n)}
                      className="shrink-0 flex flex-col justify-start items-center gap-1"
                    >
                      <div
                        className={`bg-general-background border ani ${
                          pageNumber === n ? "border-general-primary" : "border-general-border"
                        }`}
                      >
                        <Page pageNumber={n} width={80} renderTextLayer={false} renderAnnotationLayer={false} />
                      </div>
                      <div className="text-center text-general-muted-foreground text-sm font-normal leading-5">
                        {n}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {showThumbnails && numPages > 0 && <div className="w-px self-stretch bg-general-border" />}

              <div className="app-scrollbar flex-1 self-stretch p-6 bg-[#F5F5F5] flex flex-col justify-start items-center gap-4 overflow-y-auto">
                {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => (
                  <div
                    key={n}
                    ref={(el) => (pageRefs.current[n] = el)}
                    className="shrink-0 bg-general-background shadow-xl"
                  >
                    <Page pageNumber={n} scale={zoom / 100} renderTextLayer={false} renderAnnotationLayer={false} />
                  </div>
                ))}
              </div>
            </Document>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(PdfViewerPopup);
